import React from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from 'Store/index';
import { Icon } from '@upsales/components';
import { useTranslation } from 'Components/Helpers/translate';
import { openDrawer } from 'App/babel/services/Drawer';
import { isProductNewsEnabled } from 'App/helpers/productNewsPage';
import { markProductNewsNoticeSeen, markProductNewsSeen } from 'App/helpers/productNewsSeen';
import NavbarButton from '../NavbarButton';
import './NavbarProductNews.scss';

/**
 * The release notes, behind a bullhorn next to the bell, counting the ones not seen yet.
 * A notice set from Master gets a yellow dot instead, until the reader opens the drawer.
 */
const NavbarProductNews = () => {
	const { t } = useTranslation();
	const publishedIds = useSelector(({ App }: RootState) => App.metadata?.publishedReleaseNoteIds) ?? [];
	const lastSeenId = useSelector(({ App }: RootState) => App.self?.userParams.lastSeenReleaseNoteId) ?? 0;
	const notice = useSelector(({ App }: RootState) => App.metadata?.productNewsNotice) ?? null;
	const lastSeenNoticeId = useSelector(({ App }: RootState) => App.self?.userParams.lastSeenProductNewsNoticeId) ?? 0;
	const hasReleases = isProductNewsEnabled() && publishedIds.length > 0;

	if (!hasReleases && !notice) {
		return null;
	}

	const unseen = hasReleases ? publishedIds.filter(id => id > lastSeenId).length : 0;
	const newNotice = !!notice && notice.id > lastSeenNoticeId;
	const open = () => {
		if (notice && newNotice) {
			markProductNewsNoticeSeen(notice.id);
		}
		// Every published release, so the backlog counts as seen too.
		const highest = Math.max(...publishedIds, lastSeenId);
		openDrawer('ProductNewsDrawer', { notice, hasReleases, onSeen: () => markProductNewsSeen(highest) });
	};

	return (
		<NavbarButton className="navbar-product-news" title={t('productNews.title')} onClick={open}>
			<Icon name="bullhorn" color="super-light-green" />
			{newNotice ? <div className="navbar-product-news__notice-dot" /> : null}
			{unseen ? <div className="notification-badge">{unseen > 99 ? '99+' : unseen}</div> : null}
		</NavbarButton>
	);
};

export default NavbarProductNews;
